import { FunctionComponent, ReactNode, useEffect, useRef } from 'react';
import styles from './ParallaxHero.module.css';
import ParallaxLayer, { FloatAnim } from './ParallaxLayer';
import { lerp, normalizePointer, Pointer } from './parallax';

import sky from './img/layers/sky.jpg';
import distantFlyers from './img/layers/distant-flyers.png';
import backMountains from './img/layers/back-mountains.png';
import clouds1 from './img/layers/clouds-1.png';
import midMountains from './img/layers/mid-mountains.png';
import clouds2 from './img/layers/clouds-2.png';
import clouds3 from './img/layers/clouds-3.png';
import horizonHill from './img/layers/horizon-hill.png';
import pillar from './img/layers/pillar.png';
import grassyGround from './img/layers/grassy-ground.png';
import camelDrakes from './img/layers/camel-drakes.png';
import smallFlyers from './img/layers/small-flyers.png';
import foregroundCliff from './img/layers/foreground-cliff.png';
import soulstone from './img/layers/soulstone.png';

/** Back-to-front stack. `depth` drives pointer drift; back layers move least. */
interface LayerDef {
  src: string;
  depth: number;
  float?: FloatAnim;
  floatDelay?: number;
}

const LAYERS: LayerDef[] = [
  { src: sky, depth: 0 },
  { src: distantFlyers, depth: 0.2, float: 'bob', floatDelay: -2 },
  { src: backMountains, depth: 0.35 },
  { src: clouds1, depth: 0.5, float: 'drift' },
  { src: midMountains, depth: 0.7 },
  { src: clouds2, depth: 0.9, float: 'drift', floatDelay: -8 },
  { src: clouds3, depth: 1.1, float: 'drift', floatDelay: -16 },
  { src: horizonHill, depth: 1.3 },
  { src: pillar, depth: 1.6 },
  { src: grassyGround, depth: 1.9 },
  { src: camelDrakes, depth: 2.2 },
  { src: smallFlyers, depth: 2.5, float: 'bob', floatDelay: -4 },
  { src: foregroundCliff, depth: 2.9 },
  { src: soulstone, depth: 3.3, float: 'pulse' },
];

/** Max horizontal drift in px at depth 1, full pointer deflection. */
const STRENGTH = 16;

interface ParallaxHeroProps {
  children: ReactNode;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Full-screen parallax backdrop. A single requestAnimationFrame loop smooths the
 * pointer and writes the result to two CSS variables (--mx/--my, in px) on the
 * scene element. Each layer reads those and scales them by its own --depth in pure
 * CSS, so the browser composites everything on the GPU and React does zero work per
 * frame. The loop parks itself once motion settles and restarts on the next move.
 */
const ParallaxHero: FunctionComponent<ParallaxHeroProps> = ({ children }) => {
  const sceneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || prefersReducedMotion()) return;

    const target: Pointer = { x: 0, y: 0 };
    const current: Pointer = { x: 0, y: 0 };
    let raf = 0;
    let running = false;

    const tick = () => {
      current.x = lerp(current.x, target.x, 0.09);
      current.y = lerp(current.y, target.y, 0.09);
      scene.style.setProperty('--mx', `${(-current.x * STRENGTH).toFixed(2)}px`);
      scene.style.setProperty('--my', `${(-current.y * STRENGTH * 0.5).toFixed(2)}px`);

      const settled =
        Math.abs(current.x - target.x) < 0.001 &&
        Math.abs(current.y - target.y) < 0.001;
      if (settled) {
        running = false;
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    const start = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(tick);
    };

    const onMove = (e: MouseEvent) => {
      const p = normalizePointer(
        e.clientX,
        e.clientY,
        window.innerWidth,
        window.innerHeight
      );
      target.x = p.x;
      target.y = p.y;
      start();
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className={styles.hero}>
      <div className={styles.scene} ref={sceneRef}>
        {LAYERS.map((layer, i) => (
          <ParallaxLayer
            key={i}
            src={layer.src}
            z={i}
            depth={layer.depth}
            float={layer.float}
            floatDelay={layer.floatDelay}
            eager={i < 8}
          />
        ))}
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
};

export default ParallaxHero;
